/*
 * Cliente del incremento 3/6 · Movimientos.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell.
 *
 * DOS RUTAS QUE NO SE PUEDEN CONFUNDIR (D-44, RN-090c, D-301):
 *   · PUT    /api/movimientos/{id}/cancelacion  → CANCELA: el renglón se queda y se
 *     ve al encender "Ver cancelados".
 *   · DELETE /api/movimientos/{id}?confirmado=true → ELIMINA: el renglón desaparece
 *     de la aplicación, también de los cancelados y de todos los totales.
 * Ya no existe un DELETE que en realidad cancela.
 */

import { crearCliente } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/movimientos');

/** Los cuatro tipos. DEVOLUCION es un tipo propio, no un ingreso (D-47). */
export const TIPOS = {
  gasto: 'GASTO',
  ingreso: 'INGRESO',
  traspaso: 'TRASPASO',
  devolucion: 'DEVOLUCION',
};

/**
 * Filtro de mensualidades (RF-095, D-101, forma fijada por D-103).
 *
 * ES UN ENUMERADO DE TRES VALORES Y NO UN BOOLEANO, y la razón está en RN-185:
 * con tres estados «ausente» y «false» dejarían de significar lo mismo, y quien
 * enviara `false` por costumbre —como se envía `incluirCancelados=false`—
 * perdería en silencio todas sus mensualidades.
 *
 * `INCLUIR` no se envía nunca desde la pantalla: volver a «Todo» significa NO
 * MANDAR EL PARÁMETRO, no mandar `INCLUIR` colgado (RN-173g).
 */
export const RENGLONES_REPARTIDOS = {
  incluir: 'INCLUIR',
  solo: 'SOLO',
  excluir: 'EXCLUIR',
};

function consulta(parametros) {
  const partes = Object.entries(parametros)
    .filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
    .map(([clave, valor]) => `${clave}=${encodeURIComponent(valor)}`);

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}

/**
 * ConsultarMovimientos (RF-053, RF-057). La respuesta viene YA ORDENADA por el
 * servidor —fecha descendente y, a igual fecha, identificador descendente— y el
 * cliente NO reordena. Los totales vienen calculados siempre sobre los activos,
 * con "Ver cancelados" encendido o apagado (D-42, D-48).
 *
 * `fechaLocal` es la fecha de negocio del dispositivo: sin rango ni periodo, el
 * servidor aplica el mes que la contiene y NUNCA suple con su propio reloj (D-06,
 * RN-086b).
 */
export function consultarMovimientos({
  desde,
  hasta,
  idPeriodo,
  fechaLocal,
  tipo,
  idCategoria,
  idCuenta,
  idUsuarioPropietario,
  idMoneda,
  texto,
  incluirCancelados = false,
  // [NUEVO 2026-09-03 · RF-095] `SOLO`, `EXCLUIR` o nada. Sin valor NO VIAJA en la
  // cadena de consulta —`consulta()` descarta undefined y ''— y la respuesta es la
  // de siempre: es aditivo puro (RN-172, RN-185a).
  renglonesRepartidos,
  pagina = 1,
  tamanoPagina = 50,
} = {}) {
  return api.obtener(`/${consulta({
    desde, hasta, idPeriodo, fechaLocal, tipo, idCategoria, idCuenta,
    idUsuarioPropietario, idMoneda, texto, incluirCancelados, renglonesRepartidos,
    pagina, tamanoPagina,
  })}`);
}

/** ConsultarMovimiento (RF-054). Devuelve el movimiento sea de quien sea (RN-027). */
export function obtenerMovimiento(idMovimiento) {
  return api.obtener(`/${idMovimiento}`);
}

/**
 * RegistrarMovimiento (RF-048, RF-049, RF-063): gasto, ingreso o devolución.
 * El traspaso NO entra por aquí: tiene su propia operación.
 */
export function registrarMovimiento(peticion) {
  return api.publicar('/', peticion);
}

/**
 * RegistrarTraspaso (RF-050, RF-062). Traspaso, pago de tarjeta y disposición de
 * efectivo SON LA MISMA OPERACIÓN: la pantalla cambia de nombre y de textos, el
 * contrato no (D-38, D-46).
 */
export function registrarTraspaso(peticion) {
  return api.publicar('/traspasos', peticion);
}

/** EditarMovimiento (RF-051). El tipo NO se puede cambiar (D-35, RN-073). */
export function editarMovimiento(idMovimiento, peticion) {
  return api.actualizar(`/${idMovimiento}`, peticion);
}

/**
 * CancelarMovimiento (RF-052, D-34). CONSERVA el renglón con su marca; se ve al
 * encender "Ver cancelados". Sin `confirmado` el servidor responde
 * CONFIRMACION_REQUERIDA con el efecto en el saldo (RN-080e).
 */
export function cancelarMovimiento(idMovimiento, { confirmado = false, motivo = null, version } = {}) {
  return api.actualizar(`/${idMovimiento}/cancelacion`, { confirmado, motivo, version });
}

/**
 * EliminarMovimiento (RF-200, D-301, D-302). El movimiento deja de existir PARA LA
 * APLICACIÓN: no sale en la lista, ni con "Ver cancelados", ni en ningún total, y
 * NO se puede recuperar (D-303). La fila se conserva en la base con su rastro,
 * pero eso es asunto del servidor: aquí no hay nada que lo revele.
 *
 * MISMA RUTA Y MISMO VERBO que el borrado que sustituye (RNF-111): para este
 * cliente la llamada no cambió de forma, solo gana el `motivo` opcional (RF-204).
 *
 * Sin `confirmado` el servidor responde CONFIRMACION_REQUERIDA con el impacto en
 * el saldo YA RESUELTO y con la variante decidida —cambia o no cambia— en sus
 * renglones de detalle (RN-598, RNF-038). La pantalla no deduce nada de eso.
 */
export function eliminarMovimiento(idMovimiento, { confirmado = false, motivo = null, version } = {}) {
  return api.eliminar(`/${idMovimiento}${consulta({ confirmado, motivo, version })}`);
}
